/**
 * Watching an import that is happening somewhere else.
 *
 * The import is asynchronous **by design** — in-process behind a job
 * abstraction today, on a queue later — so a dataset that has just been
 * confirmed is `processing`, not done, and no screen may assume otherwise. This
 * hook is how every screen finds out what happened: it polls
 * `GET /datasets/:id/status` for the ids that are still in flight and stops the
 * moment each one reaches `ready` or `failed`.
 *
 * Why a timer rather than a socket: there is no message queue in v1 and no
 * push channel to subscribe to. `/status` is a deliberately small payload for
 * exactly this reason, and the API publishes progress after every batch, so the
 * counts move rather than sitting frozen. If a transport is added later, only
 * this file changes.
 *
 * Three rules it keeps:
 *
 *   - **It stops.** An id leaves the poll set as soon as it settles, and the
 *     timer is not rescheduled once the set is empty. Nothing polls forever.
 *   - **It gives up out loud.** After {@link MAX_CONSECUTIVE_FAILURES} failed
 *     rounds it stops and reports the error, so a dead API shows as an error
 *     rather than a spinner that never resolves.
 *   - **A settled import is announced once.** `onSettled` fires per id, which is
 *     what lets a screen reload the fuller data it could not poll for.
 */
import { isDatasetInFlight, type DatasetStatusResponse } from '@wend/shared';
import { useEffect, useRef, useState } from 'react';

import { isAbortError } from './api/abort';
import { fetchDatasetStatus } from './api/datasets';

/**
 * Fast enough that an import of a few thousand rows visibly moves, slow enough
 * that a list of ten in-flight datasets is not a load test.
 */
export const DATASET_POLL_INTERVAL_MS = 1_500;

/** Consecutive failed rounds before the hook stops and surfaces the error. */
const MAX_CONSECUTIVE_FAILURES = 5;

export interface DatasetStatusPolling {
  /** The freshest status seen per dataset id. Empty until the first round. */
  readonly statuses: Readonly<Record<string, DatasetStatusResponse>>;
  /** Set when polling gave up; the screen says so instead of spinning on. */
  readonly error: unknown;
}

/**
 * @param ids       datasets to watch. Pass only the ones actually in flight.
 * @param onSettled called once per id as it reaches `ready` or `failed`.
 */
export function useDatasetStatusPolling(
  ids: readonly string[],
  onSettled?: (status: DatasetStatusResponse) => void,
): DatasetStatusPolling {
  const [statuses, setStatuses] = useState<Record<string, DatasetStatusResponse>>({});
  const [error, setError] = useState<unknown>(null);

  // The callback is a fresh closure every render; the timer must not restart
  // because of that, so the latest one is read from a ref when it is needed.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  });

  // The identity of the *set*, not of the array. Re-rendering with the same ids
  // in a new array must not tear the timer down and start it again.
  const key = [...ids].sort().join(',');

  useEffect(() => {
    if (key === '') {
      setError(null);
      return;
    }

    const controller = new AbortController();
    const pending = new Set(key.split(','));

    let stopped = false;
    let failures = 0;
    let timer: number | undefined;

    const schedule = (): void => {
      if (stopped || pending.size === 0) return;
      timer = window.setTimeout(() => {
        void round();
      }, DATASET_POLL_INTERVAL_MS);
    };

    const round = async (): Promise<void> => {
      const targets = [...pending];
      const results = await Promise.allSettled(
        targets.map((id) => fetchDatasetStatus(id, controller.signal)),
      );
      if (stopped) return;

      const fresh: Record<string, DatasetStatusResponse> = {};
      const settled: DatasetStatusResponse[] = [];
      let roundFailure: unknown = null;

      results.forEach((result, index) => {
        const id = targets[index];
        if (id === undefined) return;

        if (result.status === 'rejected') {
          // The unmount already reported itself through `stopped`; an abort here
          // is this hook's own doing and is not a failure of the import.
          if (!isAbortError(result.reason) && roundFailure === null) {
            roundFailure = result.reason;
          }
          return;
        }

        fresh[id] = result.value;
        if (!isDatasetInFlight(result.value.status)) {
          pending.delete(id);
          settled.push(result.value);
        }
      });

      if (Object.keys(fresh).length > 0) {
        setStatuses((current) => ({ ...current, ...fresh }));
      }

      if (roundFailure === null) {
        failures = 0;
        setError(null);
      } else {
        failures += 1;
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          stopped = true;
          setError(roundFailure);
          return;
        }
      }

      for (const status of settled) {
        onSettledRef.current?.(status);
      }

      schedule();
    };

    schedule();

    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [key]);

  return { statuses, error };
}
